import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { BookOpen, ChevronDown, ChevronUp, Calendar, Star, ScrollText, Clock, User, Trophy, Users, Award, TrendingUp, Flag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const HistoryYearCard = ({ year, isExpanded, onToggle }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#2BAEE5] transition-colors" data-testid={`history-${year.year}`}>
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg bg-[#7C2C3B] flex items-center justify-center">
            <span className="text-white font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>{year.year}</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
              {year.year} SEASON
            </h3>
            {year.highlights && year.highlights.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {year.highlights.slice(0, 2).map((highlight, idx) => (
                  <span key={idx} className="text-xs text-slate-500 flex items-center gap-1">
                    <Star className="w-3 h-3 text-[#F8D742]" />
                    {highlight}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-slate-100">
          {year.image_url && (
            <img 
              src={year.image_url} 
              alt={`West Ham ${year.year}`}
              className="w-full h-48 object-cover rounded-lg mt-4 mb-4"
            />
          )}
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{year.content}</p>
          </div>
          {year.highlights && year.highlights.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-100">
              <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">Key Highlights</h4>
              <div className="flex flex-wrap gap-2">
                {year.highlights.map((highlight, idx) => (
                  <span key={idx} className="badge-blue">{highlight}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StoryCard = ({ story, isExpanded, onToggle }) => {
  const categoryIcons = {
    player: <User className="w-4 h-4" />,
    manager: <User className="w-4 h-4" />,
    match: <Trophy className="w-4 h-4" />,
    season: <Calendar className="w-4 h-4" />,
    origins: <Star className="w-4 h-4" />,
    stadium: <BookOpen className="w-4 h-4" />,
    war: <Clock className="w-4 h-4" />,
    transfer: <User className="w-4 h-4" />,
    culture: <BookOpen className="w-4 h-4" />,
    social: <User className="w-4 h-4" />,
  };

  const categoryColors = {
    player: "bg-[#2BAEE5]",
    manager: "bg-[#7C2C3B]",
    match: "bg-green-600",
    season: "bg-purple-600",
    origins: "bg-amber-600",
    stadium: "bg-slate-600",
    war: "bg-red-700",
    transfer: "bg-orange-600",
    culture: "bg-indigo-600",
    social: "bg-pink-600",
  };

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#2BAEE5] transition-all cursor-pointer"
      onClick={onToggle}
      data-testid={`story-${story.id}`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#7C2C3B] to-[#5A1F2C] flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{story.year}</span>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`${categoryColors[story.category] || 'bg-slate-600'} text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1`}>
                {categoryIcons[story.category]}
                {story.category?.toUpperCase()}
              </span>
              <span className="text-xs text-slate-400">{story.subject}</span>
            </div>
            
            <h3 className="text-lg font-bold text-[#0F172A] leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              {story.title}
            </h3>
            
            <p className={`text-slate-600 text-sm mt-2 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
              {story.story}
            </p>
            
            {!isExpanded && (
              <button className="text-[#2BAEE5] text-sm font-medium mt-2 hover:underline">
                Read more →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ManagerCard = ({ manager, isExpanded, onToggle }) => {
  const nationalityFlags = {
    "English": "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
    "Scottish": "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
    "Italian": "🇮🇹",
    "Israeli": "🇮🇱",
    "Croatian": "🇭🇷",
    "Spanish": "🇪🇸",
    "Portuguese": "🇵🇹",
  };

  const isCurrentManager = manager.tenure_end === null;

  return (
    <div 
      className={`bg-white rounded-xl border overflow-hidden transition-all ${isCurrentManager ? 'border-[#2BAEE5] ring-2 ring-[#2BAEE5]/20' : 'border-slate-200 hover:border-[#7C2C3B]'}`}
      data-testid={`manager-${manager.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${isCurrentManager ? 'bg-gradient-to-br from-[#2BAEE5] to-[#1A8FC0]' : 'bg-gradient-to-br from-[#7C2C3B] to-[#5A1F2C]'}`}>
            <Users className="w-8 h-8 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                {manager.name}
              </h3>
              <span className="text-lg">{nationalityFlags[manager.nationality] || "🏳️"}</span>
              {isCurrentManager && (
                <span className="bg-[#2BAEE5] text-white text-xs px-2 py-0.5 rounded-full">CURRENT</span>
              )}
            </div>
            <p className="text-slate-500 text-sm">{manager.years}</p>
            <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
              <span>{manager.games} games</span>
              <span>{manager.win_percentage}% win rate</span>
              {manager.honours && manager.honours.length > 0 && (
                <span className="flex items-center gap-1 text-[#F8D742]">
                  <Trophy className="w-3 h-3" />
                  {manager.honours.length} honour{manager.honours.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-slate-100">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>{manager.games}</p>
              <p className="text-xs text-slate-500 uppercase">Games</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Barlow Condensed' }}>{manager.wins}</p>
              <p className="text-xs text-slate-500 uppercase">Wins</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Barlow Condensed' }}>{manager.draws}</p>
              <p className="text-xs text-slate-500 uppercase">Draws</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Barlow Condensed' }}>{manager.losses}</p>
              <p className="text-xs text-slate-500 uppercase">Losses</p>
            </div>
          </div>

          {/* Win Percentage Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Win Rate</span>
              <span>{manager.win_percentage}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500"
                style={{ width: `${manager.win_percentage}%` }}
              />
            </div>
          </div>

          {/* Honours */}
          {manager.honours && manager.honours.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#F8D742]" />
                Honours
              </h4>
              <div className="flex flex-wrap gap-2">
                {manager.honours.map((honour, idx) => (
                  <span key={idx} className="bg-[#F8D742]/20 text-[#7C2C3B] text-sm px-3 py-1 rounded-full font-medium">
                    {honour}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Biography */}
          <div className="prose prose-slate max-w-none">
            <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">Biography</h4>
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap text-sm">{manager.biography}</p>
          </div>

          {/* Tenure Info */}
          <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {manager.tenure_start} to {manager.tenure_end || 'Present'}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {manager.duration}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default function HistoryPage() {
  const [history, setHistory] = useState([]);
  const [stories, setStories] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [managersLoading, setManagersLoading] = useState(true);
  const [expandedYear, setExpandedYear] = useState(null);
  const [expandedStory, setExpandedStory] = useState(null);
  const [expandedManager, setExpandedManager] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [activeTab, setActiveTab] = useState("timeline");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    fetchHistory();
    fetchStories();
    fetchManagers();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`);
      setStories(response.data);
    } catch (error) {
      console.error("Failed to fetch stories:", error);
    } finally {
      setStoriesLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/managers`);
      setManagers(response.data);
    } catch (error) {
      console.error("Failed to fetch managers:", error);
    } finally {
      setManagersLoading(false);
    }
  };

  const displayedHistory = showAll ? history : history.slice(0, 10);
  
  const categories = ["all", ...new Set(stories.map(s => s.category))];
  const filteredStories = categoryFilter === "all" 
    ? stories 
    : stories.filter(s => s.category === categoryFilter);

  // Calculate manager stats
  const totalGames = managers.reduce((sum, m) => sum + (m.games || 0), 0);
  const totalWins = managers.reduce((sum, m) => sum + (m.wins || 0), 0);
  const trophyWinners = managers.filter(m => m.honours && m.honours.length > 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="history-page">
      <SEO title="Club History" path="/history" description="Explore the rich history of West Ham United. From the Thames Ironworks to the London Stadium, discover key moments, legendary managers and iconic seasons." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1698277329466-2f14b1b5cb89?auto=format&fit=crop&w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            CLUB HISTORY
          </h1>
          <p className="text-white/80">Over 100 years of West Ham United history</p>
        </div>
      </div>

      {/* Timeline Intro */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-[#7C2C3B] flex items-center justify-center">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                THE HAMMERS STORY
              </h2>
              <p className="text-slate-600 mt-1">
                Founded in 1895 as Thames Ironworks F.C., West Ham United has a rich history of success, 
                memorable moments, and passionate supporters. Explore each year in detail.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 w-full justify-start">
            <TabsTrigger value="timeline" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              TIMELINE
            </TabsTrigger>
            <TabsTrigger value="stories" className="flex items-center gap-2" data-testid="stories-tab">
              <ScrollText className="w-4 h-4" />
              STORIES ({stories.length})
            </TabsTrigger>
            <TabsTrigger value="managers" className="flex items-center gap-2" data-testid="managers-tab">
              <Users className="w-4 h-4" />
              MANAGERS ({managers.length})
            </TabsTrigger>
          </TabsList>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : history.length === 0 ? (
              <div className="empty-state">
                <BookOpen className="w-16 h-16 text-slate-300 mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>NO HISTORY ENTRIES YET</h3>
                <p className="text-slate-500">Admin can add history entries via the admin panel with AI-generated content.</p>
              </div>
            ) : (
              <>
                {/* Timeline */}
                <div className="relative">
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#7C2C3B] to-[#2BAEE5]"></div>
                  
                  <div className="space-y-4">
                    {displayedHistory.map((year) => (
                      <div key={year.id} className="relative pl-20">
                        <div className="absolute left-6 top-8 w-4 h-4 rounded-full bg-[#7C2C3B] border-4 border-white shadow"></div>
                        <HistoryYearCard
                          year={year}
                          isExpanded={expandedYear === year.id}
                          onToggle={() => setExpandedYear(expandedYear === year.id ? null : year.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {history.length > 10 && (
                  <div className="text-center mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setShowAll(!showAll)}
                      className="border-[#7C2C3B] text-[#7C2C3B] hover:bg-[#7C2C3B] hover:text-white"
                      data-testid="show-more-history"
                    >
                      {showAll ? "Show Less" : `Show All ${history.length} Years`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Stories Tab */}
          <TabsContent value="stories" data-testid="stories-content">
            {storiesLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : stories.length === 0 ? (
              <div className="empty-state">
                <ScrollText className="w-16 h-16 text-slate-300 mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>NO STORIES YET</h3>
                <p className="text-slate-500">Historical stories will appear here once loaded.</p>
              </div>
            ) : (
              <>
                {/* Category Filter */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setCategoryFilter(cat)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          categoryFilter === cat
                            ? 'bg-[#7C2C3B] text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`filter-${cat}`}
                      >
                        {cat === "all" ? "All Stories" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stories Header */}
                <div className="bg-gradient-to-r from-[#7C2C3B] to-[#5A1F2C] rounded-xl p-6 mb-6 text-white">
                  <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    HISTORICAL STORIES (1895-2024)
                  </h3>
                  <p className="text-white/80 text-sm">
                    {filteredStories.length} stories from the club's rich history - featuring legendary players, 
                    iconic matches, fan memories, and unforgettable moments from Thames Ironworks to the modern era.
                  </p>
                </div>

                {/* Stories Grid */}
                <div className="space-y-4">
                  {filteredStories.map((story) => (
                    <StoryCard
                      key={story.id}
                      story={story}
                      isExpanded={expandedStory === story.id}
                      onToggle={() => setExpandedStory(expandedStory === story.id ? null : story.id)}
                    />
                  ))}
                </div>

                {/* Info Box */}
                <div className="mt-8 bg-[#2BAEE5]/10 border border-[#2BAEE5]/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2BAEE5] flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
                        ABOUT THESE STORIES
                      </h4>
                      <p className="text-sm text-slate-600">
                        These stories celebrate West Ham United's heritage from the Thames Ironworks era 
                        through to the 1980 FA Cup triumph. Each tale captures the spirit, passion, and 
                        character that defines the Hammers - from Vic Watson's goalscoring feats to Bobby 
                        Moore lifting the World Cup.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Managers Tab */}
          <TabsContent value="managers" data-testid="managers-content">
            {managersLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : managers.length === 0 ? (
              <div className="empty-state">
                <Users className="w-16 h-16 text-slate-300 mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>NO MANAGERS DATA</h3>
                <p className="text-slate-500">Manager data will appear here once loaded.</p>
              </div>
            ) : (
              <>
                {/* Managers Header */}
                <div className="bg-gradient-to-r from-[#7C2C3B] to-[#5A1F2C] rounded-xl p-6 mb-6 text-white">
                  <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    WEST HAM UNITED MANAGERS (1902-PRESENT)
                  </h3>
                  <p className="text-white/80 text-sm">
                    {managers.length} managers have led West Ham United through more than a century of football. 
                    From Syd King's foundational 30-year tenure to David Moyes' European triumph.
                  </p>
                </div>

                {/* Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-[#7C2C3B]" style={{ fontFamily: 'Barlow Condensed' }}>{managers.length}</p>
                    <p className="text-xs text-slate-500 uppercase mt-1">Managers</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>{totalGames.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 uppercase mt-1">Total Games</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Barlow Condensed' }}>{totalWins.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 uppercase mt-1">Total Wins</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>{trophyWinners.length}</p>
                    <p className="text-xs text-slate-500 uppercase mt-1">Trophy Winners</p>
                  </div>
                </div>

                {/* Managers List */}
                <div className="space-y-4">
                  {managers.map((manager) => (
                    <ManagerCard
                      key={manager.id}
                      manager={manager}
                      isExpanded={expandedManager === manager.id}
                      onToggle={() => setExpandedManager(expandedManager === manager.id ? null : manager.id)}
                    />
                  ))}
                </div>

                {/* Info Box */}
                <div className="mt-8 bg-[#2BAEE5]/10 border border-[#2BAEE5]/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2BAEE5] flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
                        THE WEST HAM WAY
                      </h4>
                      <p className="text-sm text-slate-600">
                        West Ham's managerial history is defined by those who embraced the "West Ham Way" - 
                        attractive, technical football that prioritizes skill and creativity. From Ron Greenwood's 
                        revolutionary coaching to David Moyes' European triumph, the club has been shaped by 
                        managers who understood its unique identity.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <div className="py-8"></div>
      <Footer />
    </div>
  );
}
